import type { Reducer } from 'redux';
import type { SciTradeInComponentState } from '../types/TradeInState';

// consts/enums
import { Actions } from './actions';
import type { VehicleOptions } from '../types/ITradeinActivity';

// state
import isEmpty from 'lodash.isempty';
import { mapToTradeIn } from '@makemydeal/dr-shared-ui-utils';
import { getInitialState, initialVehicle } from '../store/initialStates/initialTradeInState';
import { getTradeInCompletionStatus, isInLoanFlowRoute, isLoanConfirmationAnswer, isNoLoanAnswer } from '../utils';
import { AMOUNT_OWED_SOURCE_PLACEHOLDER, CLEARED_LOAN_STEP, UNANSWERED_REGISTRATION_DETAILS } from '../constants';
import { reducer as baseTradeInReducer } from '@makemydeal/dr-activities-tradein';

import { initActionTypes } from '@makemydeal/dr-shared-store';

const isUncommittedEntry = (state: SciTradeInComponentState): boolean => !state.isCompleted && !state.tradeInSent;

/**
 * SCI Trade-In Reducer
 *
 * This reducer extends the base dr-activities-tradein reducer with SCI-specific customizations:
 * 1. Payload structure differences: SCI BFF returns data in action.payload.data format vs base's action.payload
 * 2. Completion status logic: Includes custom isCompleted flag calculations based on price values and routing
 * 3. Additional saved fields: Persists extra trade-in fields (offerDate, status, tivSource, etc.)
 *
 * All other actions are delegated to the base reducer for code reuse.
 */
export const tradeInReducer: Reducer<SciTradeInComponentState> = (state = getInitialState(), action: any) => {
    switch (action.type) {
        case Actions.REFRESH_TRADE_IN:
        case initActionTypes.INIT_PENCIL_SUCCESS: {
            if (!action.payload.deal && !action.payload.data?.deal) {
                return state;
            }

            const details = action.payload.deal ? action.payload.deal.details : { ...action.payload.data.deal };
            const tradeIn = mapToTradeIn(details, true);

            const shouldPreserveInProgressEntry = action.type === Actions.REFRESH_TRADE_IN && isUncommittedEntry(state);

            // The `any` is needed temporarily to handle dynamic state updates from mapToTradeIn until
            // full type safety can be enforced
            let next: any = state;

            if (isEmpty(tradeIn) && !shouldPreserveInProgressEntry) {
                const initialState = getInitialState() as any;
                const reset: any = {};
                Object.keys(initialState).forEach((key) => {
                    reset[key] = initialState[key] ?? null;
                });
                next = { ...next, ...reset, ...UNANSWERED_REGISTRATION_DETAILS };
            }

            if (tradeIn.isCompleted && !isEmpty(tradeIn)) {
                next = {
                    ...next,
                    amountOwed: tradeIn.amountOwed,
                    expirationDate: tradeIn.expirationDate,
                    shouldIncludeTradeIn: tradeIn.shouldIncludeTradeIn || tradeIn.isAmountAppliedToFinancing,
                    value: tradeIn.value,
                    ownership: tradeIn.ownershipType,
                    vehicleOptions: tradeIn.vehicleOptions,
                    // map vehicle data
                    vehicle: {
                        ...next.vehicle,
                        year: `${tradeIn.year}`,
                        make: { id: tradeIn.makeId, name: tradeIn.make },
                        model: { id: tradeIn.modelId, name: tradeIn.model },
                        trim: {
                            id: tradeIn.trimId,
                            name: tradeIn.trim,
                            vehicleId: tradeIn.vehicleId || next.vehicle?.trim?.vehicleId || ''
                        },
                        mileage: tradeIn.mileage,
                        vin: tradeIn.vin,
                        color: tradeIn.color,
                        interiorColor: tradeIn.interiorColor,
                        condition: tradeIn.condition
                    },
                    isCompleted: true,
                    tradeInSent: true,
                    source: tradeIn.source || '',
                    zip: tradeIn.zip || next.zip || '',
                    lienHolderAddress: tradeIn.lienHolderAddress || null,
                    lienHolderAccountNumber: tradeIn.lienHolderAccountNumber || '',
                    lienHolderPhoneNumber: tradeIn.lienHolderPhoneNumber || '',
                    amountOwedSource: tradeIn.lienHolder || AMOUNT_OWED_SOURCE_PLACEHOLDER,
                    isBuyerRegisteredOwner: tradeIn.isBuyerRegisteredOwner,
                    isSellingVehicleRegisteredInSameState: tradeIn.isSellingVehicleRegisteredInSameState,
                    hasOpenLoan: tradeIn.amountOwed > 0 ? true : null
                };
            }

            return { ...next, ...CLEARED_LOAN_STEP };
        }
        case Actions.UPDATE_CURRENT_CUSTOMER:
        case Actions.UPDATE_PARTICIPANT_DETAILS: {
            if (action.payload.participant === 'coBuyer') {
                return state;
            }

            return {
                ...state,
                zip: action.payload.customer?.postalCode || ''
            };
        }

        case Actions.REMOVE_TRADE_IN:
        case Actions.RESET_TRADE_IN: {
            return { ...getInitialState() };
        }

        case Actions.TRADE_IN_LOAN_FLOW_PERSISTED: {
            return { ...state, ...CLEARED_LOAN_STEP };
        }

        case Actions.UPDATE_TRADE_IN_BALANCE_OWED: {
            const next = baseTradeInReducer(state, action);
            return isNoLoanAnswer(state, action.payload.amountOwed) ? { ...next, loanStepAnswered: true } : next;
        }

        case Actions.TRADE_IN_SKIPPED: {
            return {
                ...state,
                isCompleted: false,
                skippedTradeIn: true
            };
        }

        case Actions.TRADE_IN_COMPLETED_CONTINUE: {
            return {
                ...state,
                hasTradeInChanged: false,
                isCompleted: true,
                skippedTradeIn: false
            };
        }

        case Actions.TRADE_IN_OPTIONS_SUCCESS: {
            return {
                ...state,
                vehicleOptions: { ...(action.payload.data || []) },
                conflicts: []
            };
        }

        case Actions.TRADE_IN_YEARS_SUCCESS: {
            return {
                ...state,
                lookups: {
                    ...state.lookups,
                    years: {
                        options: [...(action.payload.data || [])],
                        isLoading: false
                    }
                }
            };
        }

        case Actions.TRADE_IN_MAKES_SUCCESS: {
            return {
                ...state,
                lookups: {
                    ...state.lookups,
                    makes: {
                        options: [...(action.payload.data || [])],
                        isLoading: false
                    }
                }
            };
        }

        case Actions.TRADE_IN_MODELS_SUCCESS: {
            return {
                ...state,
                lookups: {
                    ...state.lookups,
                    models: {
                        options: [...(action.payload.data || [])],
                        isLoading: false
                    }
                }
            };
        }

        case Actions.TRADE_IN_TRIMS_SUCCESS: {
            return {
                ...state,
                lookups: {
                    ...state.lookups,
                    trims: {
                        options: [...(action.payload.data || [])],
                        isLoading: false
                    }
                }
            };
        }

        case Actions.TRADE_IN_VEHICLES_SUCCESS: {
            return {
                ...state,
                vehicle: {
                    ...state.vehicle,
                    trim: {
                        id: action.payload.data?.trimId?.toString() || '',
                        name: action.payload.data?.trimName || '',
                        vehicleId: `${action.payload.data?.vehicleId}`
                    }
                }
            };
        }

        case Actions.TRADE_IN_ESTIMATION_SUCCESS: {
            const estimate = [...(action.payload?.data ?? [])];

            let newState = {
                ...state,
                estimate,
                isEquipOptionsEstimate1InProgress: false
            };

            if (action.payload?.data && action.payload.data.tivSource !== 'ICO') {
                const value = estimate.find((item: any) => item.selected)?.value || 0;
                const isCompleted = getTradeInCompletionStatus(state.currentUserLocationRoute, value);
                newState = {
                    ...newState,
                    value,
                    isCompleted: state.isCompleted || isCompleted
                };
            }

            return newState;
        }

        case Actions.TRADE_IN_SAVE_OR_UPDATE: {
            // "Submit for Appraisal" flow - mark as completed when on manual entry with value
            const isCompleted = getTradeInCompletionStatus(state.currentUserLocationRoute, state.value);
            return {
                ...state,
                isCompleted: state.isCompleted || isCompleted,
                ...(isLoanConfirmationAnswer(state) ? { loanStepAnswered: true } : {})
            };
        }

        case Actions.UPDATE_TRADE_IN_CURRENT_LOCATION: {
            return {
                ...state,
                previousUserLocationRoute: state.currentUserLocationRoute,
                currentUserLocationRoute: action.payload.nextRoute,
                ...(isInLoanFlowRoute(action.payload.nextRoute) ? { loanStepPending: true } : {})
            };
        }

        case Actions.UPDATE_TRADE_IN: {
            return {
                ...state,
                saved: {
                    ...state.saved,
                    location: state.location,
                    vehicle: state.vehicle || initialVehicle,
                    vehicleOptions: state.vehicleOptions as VehicleOptions,
                    amountOwed: state.amountOwed || 0,
                    estimate: [...(state.estimate || [])],
                    shouldIncludeTradeIn: state.shouldIncludeTradeIn,
                    ownership: state.ownership || '',
                    value: state.value || 0,
                    valueSource: state.valueSource || '',
                    valueComments: state.valueComments || '',
                    zip: state.zip || '',
                    offerDate: state.offerDate || '',
                    offerExpirationDate: state.tivOfferExpirationDate || '',
                    status: state.status || '',
                    tivSource: state.tivSource || ''
                }
            };
        }
        case Actions.SUBARU_ELIGIBILITY_SUMMARY_CHECK_SUCCESS: {
            const { price } = action.payload;
            if (typeof price === 'number') {
                return {
                    ...state,
                    value: price,
                    isCompleted: price != null
                };
            }
            return state;
        }
        case Actions.TRADE_IN_VALIDATE_OPTIONS_SUCCESS: {
            const actionTyped = action;
            return {
                ...state,
                vehicleOptions: actionTyped.payload.data.kbbOptions
            };
        }
        case Actions.TRADE_IN_ADD_ICO_VALUE: {
            return {
                ...state,
                isCompleted: state.value != null
            };
        }
        default:
            // Delegate all other actions to the base reducer from dr-activities-tradein
            return baseTradeInReducer(state, action);
    }
};
